import type {
  ApiError,
  ChannelCloseoutOptions,
  ChannelCloseoutPreview,
  ChannelCloseoutWriteRequest,
} from '@/shared/api';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { closeoutSentence, listGerman } from './channelCloseoutPresentation';

interface CloseoutCheck {
  result: ChannelCloseoutPreview | null;
  problem: ApiError | null;
  retry: () => void;
}

/**
 * Die Regel im Klartext und das Ergebnis der Server-Prüfung. Überschneidungen sind erlaubt — beide
 * Regeln bleiben wirksam —, sie werden nur genannt. Beim Bearbeiten zählt die Regel selbst nicht mit.
 */
export function ChannelCloseoutSummary({
  draft,
  apartments,
  ready,
  check,
  closeoutId,
}: {
  draft: ChannelCloseoutWriteRequest;
  apartments: ChannelCloseoutOptions['apartments'];
  ready: boolean;
  check: CloseoutCheck;
  closeoutId: string | null;
}) {
  const names = apartments
    .filter((apartment) => draft.propertyIds.includes(apartment.propertyId))
    .map((apartment) => apartment.name);
  if (draft.channels.length === 0 || names.length === 0 || !draft.startDate || !draft.endDate) {
    return (
      <p className="text-ink-faint text-[13px]">
        Wähle Kanal, Apartments und Zeitraum. Dann prüft der Server die Regel, bevor du sie
        vormerkst.
      </p>
    );
  }
  const overlaps = check.result?.overlaps.filter((item) => item.id !== closeoutId) ?? [];
  return (
    <section
      aria-label="Zusammenfassung"
      className="border-hairline bg-card rounded-control space-y-3 border p-4"
    >
      <p className="text-ink text-sm leading-relaxed">
        {closeoutSentence(draft.channels, names, draft.startDate, draft.endDate)}
      </p>
      {check.problem ? (
        <div role="alert" className="space-y-2">
          <p className="text-sealwax text-[13px]">
            Prüfung fehlgeschlagen: {check.problem.detail ?? check.problem.title}
          </p>
          <Button type="button" variant="ghost" onClick={check.retry}>
            Erneut prüfen
          </Button>
        </div>
      ) : (
        <p
          role="status"
          className={cn(
            'text-[13px]',
            !check.result ? 'text-ink-soft' : overlaps.length === 0 ? 'text-forest' : 'text-ink',
          )}
        >
          {!ready
            ? 'Ohne Bezeichnung prüft der Server die Regel nicht.'
            : !check.result
              ? 'Wird vom Server geprüft …'
              : overlaps.length === 0
                ? 'Vom Server geprüft · keine Überschneidung mit anderen Regeln.'
                : `Vom Server geprüft · überschneidet sich mit ${listGerman(overlaps.map((item) => `„${item.name}“`))}. Beide Regeln bleiben wirksam.`}
        </p>
      )}
    </section>
  );
}
